import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  Typography,
} from "@mui/material";
import { AssociatedLabor } from "../../../../redux/types/Material";
import { Close } from "@mui/icons-material";

interface LaborVariantModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (variantId: number) => void;
  labor: AssociatedLabor;
  isSelecting: boolean;
}

export const LaborVariantModal: React.FC<LaborVariantModalProps> = ({
  open,
  onClose,
  onSelect,
  labor,
  isSelecting,
}) => {
  const getInitialSelected = () => {
    const selectedVariant = labor.variants?.find((v) => v.isSelected);
    if (selectedVariant) return selectedVariant.id;
    
    // Default to base labor (0) if nothing else is selected
    return 0;
  };

  const [selectedId, setSelectedId] = useState<number | null>(getInitialSelected());

  // Update selectedId if modal is opened with new props
  React.useEffect(() => {
    if (open) {
      setSelectedId(getInitialSelected());
    }
  }, [open, labor]);

  const allOptions = [
    {
      id: 0,
      description: "Base labor",
      longDescription: labor.description,
      price: labor.price,
      unit: labor.measurementUnit,
      coverage: labor.coverageAmount,
    },
    ...(labor.variants || []).map((v) => ({
      id: v.id,
      description: v.description,
      longDescription: v.description,
      price: v.price,
      unit: v.measurementUnit,
      coverage: v.coverageAmount,
    })),
  ];

  return (
    <Dialog
      open={open}
      onClose={isSelecting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          padding: "8px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "20px",
          fontWeight: 600,
          color: "#1f2937",
          pb: 1,
        }}
      >
        Select Labor Option
        <Close
          onClick={isSelecting ? undefined : onClose}
          sx={{
            cursor: isSelecting ? "not-allowed" : "pointer",
            color: "#9ca3af",
            "&:hover": { color: "#4b5563" },
          }}
        />
      </DialogTitle>
      <DialogContent sx={{ pt: "8px !important" }}>
        <Typography sx={{ color: "#6b7280", mb: 3, fontSize: "14px" }}>
          {allOptions.length > 1
            ? "This contractor offers different labor variants. Please select the option that best fits this project."
            : "Please confirm the base labor selection for this contractor."}
        </Typography>

        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {allOptions.map((option) => (
            <div
              key={option.id}
              onClick={() => !isSelecting && setSelectedId(option.id)}
              style={{
                display: "flex",
                alignItems: "flex-start",
                padding: "16px",
                border: "2px solid",
                borderColor: selectedId === option.id ? "#3b82f6" : "#e5e7eb",
                borderRadius: "12px",
                cursor: isSelecting ? "not-allowed" : "pointer",
                backgroundColor: selectedId === option.id ? "#eff6ff" : "#fff",
                transition: "all 0.2s ease",
              }}
            >
              <Radio
                checked={selectedId === option.id}
                onChange={() => !isSelecting && setSelectedId(option.id)}
                value={option.id}
                name="labor-variant"
                sx={{
                  mt: "-2px",
                  p: 0,
                  mr: 2,
                  color: selectedId === option.id ? "#3b82f6" : "#d1d5db",
                  "&.Mui-checked": {
                    color: "#3b82f6",
                  },
                }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                  <Typography sx={{ fontWeight: 600, color: "#1f2937", fontSize: "15px" }}>
                    {option.description}
                  </Typography>
                  <Typography sx={{ fontWeight: 600, color: "#3b82f6", fontSize: "15px" }}>
                    ${option.price.toLocaleString()}
                  </Typography>
                </div>
                <div style={{ display: "flex", gap: "16px", color: "#6b7280", fontSize: "13px" }}>
                  <span>{option.coverage} {option.unit} coverage</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
      <DialogActions sx={{ padding: "16px 24px" }}>
        <Button
          onClick={onClose}
          disabled={isSelecting}
          sx={{
            textTransform: "none",
            color: "#6b7280",
            fontWeight: 500,
            "&:hover": { backgroundColor: "#f3f4f6" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => selectedId !== null && onSelect(selectedId)}
          disabled={selectedId === null || isSelecting}
          sx={{
            textTransform: "none",
            backgroundColor: "#3b82f6",
            color: "#fff",
            fontWeight: 500,
            padding: "8px 24px",
            borderRadius: "8px",
            "&:hover": { backgroundColor: "#2563eb" },
            "&.Mui-disabled": { backgroundColor: "#9ca3af", color: "#f3f4f6" },
          }}
        >
          {isSelecting ? "Confirming..." : "Confirm Selection"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
